import { FileMovedIcon, SearchIcon, SyncIcon } from '@primer/octicons-react';
import { useRouter } from 'next/router';
import React, { Suspense } from 'react';
import { graphql } from 'react-relay';

import BrandMessageTemplateConnectionDataTable from '../../components/brandMessageTemplate/BrandMessageTemplateConnectionDataTable';
import BrandMessageTemplateCreateDialog from '../../components/brandMessageTemplate/BrandMessageTemplateCreateDialog';
import BrandMessageTemplateWindowPaginator from '../../components/brandMessageTemplate/BrandMessageTemplateWindowPaginator';
import Button from '../../components/core/Button';
import DialogButton from '../../components/core/DialogButton';
import EmptyState from '../../components/core/EmptyState';
import ErrorBoundary from '../../components/core/ErrorBoundary';
import Grid from '../../components/core/Grid';
import Head from '../../components/core/Head';
import { HeaderSidebarNavPageLayout } from '../../components/core/Layout';
import QueryFormik from '../../components/core/QueryFormik';
import Spinner from '../../components/core/Spinner';
import Stack from '../../components/core/Stack';
import Text from '../../components/core/Text';
import View from '../../components/core/View';
import { usePaginationContext } from '../../contexts/PaginationContext';
import useInitialValuesFromParsedUrlQuery from '../../hooks/useInitialValuesFromParsedUrlQuery';
import useToast from '../../hooks/useToast';
import { brandMessageTemplate_brandMessageTemplatesQuery } from '../../relay/__generated__/brandMessageTemplate_brandMessageTemplatesQuery.graphql';
import { numberWithCommas } from '../../utils/number';
import { NextPage } from '../_app';
import BrandMessageTemplateId from './[brandMessageTemplateId]';

const brandMessageTemplatesForBrandMessageTemplate = graphql`
  query brandMessageTemplate_brandMessageTemplatesQuery(
    $filters: BrandMessageTemplateFilter
    $order: BrandMessageTemplateOrder
    $page: Int
    $pageSize: Int
  ) {
    ...BrandMessageTemplateWindowPaginator_query
      @arguments(filters: $filters, order: $order, page: $page, pageSize: $pageSize)
  }
`;
type Props = {};

const BrandMessageTemplate: NextPage<Props> = () => {
  const router = useRouter();
  const { initialValues, setParsedUrlQuery } = useInitialValuesFromParsedUrlQuery({
    search: { type: 'string' },
    order: { type: 'string' },
    page: { type: 'number' },
  });
  const { pageSize } = usePaginationContext();
  const { toast } = useToast();

  const { search, order, page } = initialValues;

  return (
    <View>
      <Head siteTitle={'브랜드 메세지 템플릿'} />
      <ErrorBoundary>
        <QueryFormik<brandMessageTemplate_brandMessageTemplatesQuery>
          query={brandMessageTemplatesForBrandMessageTemplate}
          staticVariables={{ pageSize }}
          initialValues={{
            filters: {
              search,
              isDormant_Exact: false,
            },
            order: order || '-createTime',
            page,
          }}
          options={{ fetchPolicy: 'store-and-network' }}
          onSubmit={(values) => setParsedUrlQuery({ ...values.filters, order }, { scroll: false })}
        >
          {({ values: { filters, order }, resetForm, setFieldValue, submitForm }, queryReference) => {
            const handleReset = () => {
              resetForm({ values: { order } });
              setParsedUrlQuery({ order }, { scroll: false });
            };

            return (
              <View>
                <Grid sx={{ alignItems: 'center' }}>
                  <Grid.Unit size={'max'}>
                    <Text as={'h1'}>브랜드 메세지 템플릿</Text>
                  </Grid.Unit>
                  <Grid.Unit size={'min'}>
                    <DialogButton
                      size={'large'}
                      variant={'primary'}
                      renderDialog={({ isOpen, closeDialog }) => (
                        <BrandMessageTemplateCreateDialog
                          wide
                          isOpen={isOpen}
                          onDismiss={closeDialog}
                          config={{
                            onCompleted: () => {
                              toast('제작이 완료됐어요', 'success');
                              submitForm();
                              closeDialog();
                            },
                            onError: () => {
                              toast('제작에 실패했어요', 'error');
                            },
                          }}
                        />
                      )}
                    >
                      생성하기
                    </DialogButton>
                  </Grid.Unit>
                </Grid>
                <Grid>
                  <Grid.Unit size={'min'}>
                    <QueryFormik.FilterSearchTextField
                      typename={'BrandMessageTemplateFilter'}
                      label={'Search'}
                      labelConfig={{ visuallyHidden: true }}
                      name={'filters.search'}
                      size={'large'}
                      autoComplete={'off'}
                      leadingVisual={SearchIcon}
                      debounce
                      onChange={(e) => {
                        setFieldValue('filters.search', e.target.value);
                        setTimeout(() => submitForm(), 0);
                      }}
                    />
                  </Grid.Unit>
                  <Grid.Unit size={'max'}>
                    <View sx={{ display: 'flex', justifyContent: 'end' }}>
                      <Stack gapX={2}>
                        <Stack.Item>
                          <Button leadingIcon={SyncIcon} variant={'plain'} onClick={handleReset}>
                            초기화
                          </Button>
                        </Stack.Item>
                      </Stack>
                    </View>
                  </Grid.Unit>
                </Grid>
                <View sx={{ marginTop: 5 }}>
                  <ErrorBoundary key={queryReference?.fetchKey}>
                    <Suspense fallback={<Spinner />}>
                      <QueryFormik.PreloadedQueryRenderer<brandMessageTemplate_brandMessageTemplatesQuery>>
                        {(queryReference) => (
                          <BrandMessageTemplateWindowPaginator
                            fragmentReference={queryReference}
                            onLoadPage={(page) => setParsedUrlQuery({ ...filters, order, page })}
                          >
                            {({ brandMessageTemplates }, { renderPagination }) => (
                              <>
                                <View>
                                  <Text sx={{ fontSize: 1, fontWeight: 'bold', color: 'fg.muted' }}>
                                    총 {numberWithCommas(brandMessageTemplates?.totalCount || 0)}건
                                  </Text>
                                </View>
                                <View sx={{ marginTop: 3 }}>
                                  <BrandMessageTemplateConnectionDataTable
                                    brandMessageTemplateConnection={brandMessageTemplates}
                                    onRowClick={({ id }) => router.push({ pathname: `/brandMessageTemplate/${id}` })}
                                    emptyState={
                                      <View sx={{ paddingY: 3 }}>
                                        <EmptyState
                                          title={'필터에 맞는 결과가 없어요'}
                                          description={'다른 필터로 다시 시도해보세요.'}
                                        />
                                      </View>
                                    }
                                  />
                                </View>
                                <View sx={{ display: 'flex', justifyContent: 'center', marginTop: 3 }}>
                                  {renderPagination?.()}
                                </View>
                              </>
                            )}
                          </BrandMessageTemplateWindowPaginator>
                        )}
                      </QueryFormik.PreloadedQueryRenderer>
                    </Suspense>
                  </ErrorBoundary>
                </View>
              </View>
            );
          }}
        </QueryFormik>
      </ErrorBoundary>
    </View>
  );
};

BrandMessageTemplate.getLayout = (page) => <HeaderSidebarNavPageLayout>{page}</HeaderSidebarNavPageLayout>;
BrandMessageTemplate.authenticated = true;
BrandMessageTemplate.routes = [
  {
    id: 'brandMessageTemplate',
    icon: FileMovedIcon,
    pathname: '/brandMessageTemplate',
    name: '브랜드 메세지 템플릿',
    permissions: ['app_push_read'],
  },
  ...BrandMessageTemplateId.routes,
];

export default BrandMessageTemplate;
